import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core';
import { identityTiptapFileSrcResolver, type TiptapFileSrcResolver } from '../../useTiptapFileSrc';

export interface ImageOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, any>;
  resolveFileSrc: TiptapFileSrcResolver;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    image: {
      /**
       * Add an image
       */
      setImage: (
        options: {
          src: string;
          alt?: string;
          title?: string;
          position?: number;
          fileDrn?: string;
        },
        position: number,
      ) => ReturnType;
    };
  }
}

export const inputRegex = /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;

export const Image = Node.create<ImageOptions>({
  name: 'image',

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
      resolveFileSrc: identityTiptapFileSrcResolver,
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return this.options.inline ? 'inline' : 'block';
  },

  draggable: true,

  addAttributes() {
    return {
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      width: {
        default: '80%',
      },
      fileDrn: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-file-drn'),
        renderHTML: (attributes) =>
          attributes.fileDrn ? { 'data-file-drn': attributes.fileDrn } : {},
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: this.options.allowBase64 ? 'img[src]' : 'img[src]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const { src, fileDrn } = node.attrs;
    const resolvedSrc = typeof src === 'string' ? this.options.resolveFileSrc(fileDrn, src) : src;
    return [
      'div',
      { style: HTMLAttributes.style },
      ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { src: resolvedSrc })],
    ];
  },

  addCommands() {
    return {
      setImage:
        (options, position) =>
        ({ commands }) => {
          return commands.insertContentAt(position, {
            type: this.name,
            attrs: options,
          });
        },
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, , alt, src, title] = match;

          return { src, alt, title };
        },
      }),
    ];
  },
});
